import React, { useEffect } from "react";
import "./Splash.scss";
import { Link } from "react-router-dom";

import Appbar from "../../components/Appbar/Appbar";
import Footer from "../../components/Footer/Footer";
import Form from "../../components/Form/Form";
import { useAnalytics } from "../../customHooks/reactGA";
import {
  scrollToFormAndFocusName,
  scrollToNearestFormAndFocusName,
} from "../../utils/scrollToForm";
import { FAQS } from "../../data/faqs";

import { Box, Button, Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import TravisHeadshot from "../../assets/Travis_headshot.png";
import commercial from "../../assets/commercial_placeholder.png";
import testimonialLinda from "../../assets/testimonial_linda.png";
import testimonialMarcus from "../../assets/testimonial_marcus.png";
import testimonialDiana from "../../assets/testimonial_diana.png";
import iconLight from "../../assets/carp_house_plain.png";
import iconDark from "../../assets/carp-house-plain-dark.png";
import iconWrench from "../../assets/wrench.png";
import iconWrenchDark from "../../assets/wrench_dark.png";
import iconDeadline from "../../assets/deadline.png";
import iconDeadlineDark from "../../assets/deadline_dark.png";
import iconMoney from "../../assets/money.png";
import iconMoneyDark from "../../assets/money_dark.png";
import iconTrust from "../../assets/relationship.png";
import iconTrustDark from "../../assets/relationship_dark.png";

import Commercial from "../../assets/CarpHomes_TV_Commercial_2025.mp4";

import ReactPlayer from "react-player";
import Seo from "../../components/Seo/Seo";
import { primaryCtaSx, navCtaSx } from "../../styles/ctaStyles";

const PEOPLE_CONSULTATION =
  "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80";
const PEOPLE_OFFER =
  "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80";
const PEOPLE_KEYS =
  "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&q=80";
const PEOPLE_INHERITANCE =
  "https://images.unsplash.com/photo-1543269865-cbf427effbad?w=600&q=80";
const PEOPLE_DISREPAIR =
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&q=80";
const PEOPLE_FORECLOSURE =
  "https://images.unsplash.com/photo-1590698933947-a202b069a861?w=600&q=80";
const PEOPLE_VACANT =
  "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80";
const PEOPLE_FASTCASH =
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80";

const getHeadlineFromUrl = () => {
  if (typeof window === "undefined")
    return "Get a Fair Cash Offer for Your Indianapolis House";
  const params = new URLSearchParams(window.location.search);
  const v = params.get("v");
  if (v === "asis")
    return "Sell Your Indianapolis House As-Is, No Repairs Needed";
  if (v === "fast")
    return "Sell Your Indianapolis House Fast. Close in as Little as 7 Days";
  return "Get a Fair Cash Offer for Your Indianapolis House";
};

const PHONE_NUMBER = "317-812-1388";
const PHONE_HREF = "tel:+13178121388";
const SITUATIONS_DESCRIPTION = "";

const Splash = (props) => {
  const { trackPageView, trackEvent } = useAnalytics();
  const isDark = props.theme.palette.mode === "dark";

  const [headline] = React.useState(getHeadlineFromUrl);
  const [showStickyBar, setShowStickyBar] = React.useState(true);
  const [showVideo, setShowVideo] = React.useState(false);

  useEffect(() => {
    const forms = Array.from(document.querySelectorAll("#lead-form"));
    if (forms.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const anyOnScreen = entries.some(
          (e) => e.isIntersecting && e.intersectionRatio > 0,
        );
        setShowStickyBar(!anyOnScreen);
      },
      { threshold: [0, 0.05, 0.5] },
    );

    forms.forEach((f) => observer.observe(f));
    return () => observer.disconnect();
  }, []);

  const handleStickyBarOffer = () => {
    scrollToNearestFormAndFocusName();
  };

  useEffect(() => {
    trackPageView("/");
  }, [trackPageView]);

  const featureTextColor = isDark
    ? props.theme.palette.common.white
    : props.theme.palette.primary.main;

  const handleCtaClick = (label) => (e) => {
    trackEvent("CTA", "Click", label);
    scrollToFormAndFocusName(e);
  };

  return (
    <div>
      <Seo
        title="Sell Your House Fast for Cash in Indianapolis"
        description="Get a fair cash offer for your Indianapolis house. CarpHomes buys homes directly from homeowners — close in as little as 7 days, no repairs, no agent commissions, no closing costs."
        pathname="/"
      />
      <Appbar
        colorMode={props.colorMode}
        theme={props.theme}
        mode={props.mode}
      />

      <Box
        component="section"
        className="landing landing--hero"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.primary.dark
              : "#e4e4e4",
          color:
            theme.palette.mode === "dark"
              ? theme.palette.common.white
              : theme.palette.primary.main,
          backgroundImage: `url(${theme.palette.mode === "dark" ? iconDark : iconLight})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "contain",
          px: { xs: 3, md: 7 },
          py: { xs: 3, md: 8 },
        })}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            alignItems: "center",
            gap: { xs: 3, md: 6 },
            maxWidth: "1350px",
            width: "100%",
            mx: "auto",
          }}
        >
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Typography
              component="h1"
              className="heading-title l1 heading-title--pop"
              sx={(theme) => ({
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : theme.palette.primary.main,
              })}
            >
              {headline}
            </Typography>

            <Typography
              component="p"
              className="heading-title l2"
              sx={(theme) => ({
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : theme.palette.primary.dark,
                maxWidth: "520px",
              })}
            >
              Close in as little as 7 days. No repairs, no agent commissions, no
              closing costs.
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              display: "flex",
            }}
          >
            <Box
              sx={(theme) => ({
                width: "100%",
                p: { xs: 2, md: 4 },
                borderRadius: "24px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.25)"
                    : "rgba(255, 255, 255, 0.72)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 20px 45px rgba(0, 0, 0, 0.25)"
                    : "0 20px 45px rgba(0, 0, 0, 0.12)",
                backdropFilter: "blur(4px)",
              })}
            >
              <Typography
                component="h2"
            fontFamily="karla"
            fontSize={{ xs: "1.35rem", md: "1.65rem" }}
            fontWeight={700}
            lineHeight={1.25}
                sx={(theme) => ({
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.white
                      : theme.palette.primary.main,
                  fontSize: { xs: "1.25rem", md: "2rem" },
                  fontFamily: "karla",
                  mb: 1.5,
                  textAlign: "center",
                })}
              >
                Get Your Free Cash Offer
              </Typography>

              <Form theme={props.theme} />
            </Box>
          </Box>

          <Box
            sx={{
              gridColumn: { xs: "auto", md: "1 / -1" },
              display: "flex",
              gap: 3,
              mt: { xs: 1, md: 3 },
              alignItems: "flex-start",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ minWidth: 120, textAlign: "center" }}>
              <Typography fontSize="2rem" fontWeight={700} color="warning.main">
                5★
              </Typography>
              <Typography fontSize="0.95rem" color={featureTextColor}>
                <a
                  href="https://www.google.com/search?q=carphomes+indianapolis+reviews"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", color: "inherit" }}
                >
                  5 stars on Google
                </a>
              </Typography>
            </Box>

            <Box sx={{ minWidth: 140, textAlign: "center" }}>
              <Typography fontSize="2rem" fontWeight={700} color="warning.main">
                A+
              </Typography>
              <Typography fontSize="0.95rem" color={featureTextColor}>
                <a
                  href="https://www.bbb.org/us/in/carmel/profile/real-estate-investing/carphomes-llc-0382-90060178"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", color: "inherit" }}
                >
                  A+ rating on BBB
                </a>
              </Typography>
            </Box>

            <Box sx={{ minWidth: 160, textAlign: "center" }}>
              <Typography fontSize="2rem" fontWeight={700} color="warning.main">
                60+
              </Typography>
              <Typography fontSize="0.95rem" color={featureTextColor}>
                Avg. transactions / year
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        className="offerings-container"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.primary.dark
              : "#e4e4e4",
          px: { xs: 3, md: 7 },
          py: 4,
          display: "flex",
          justifyContent: "center",
        })}
      >
        <Box className="content-wrapper">
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="row"
            flexWrap="wrap"
          >
            {[
              {
                title: "Fast Closings",
                body: "Sell your house in as little as 7 days.",
                alt: "fast closings icon",
                icon: isDark ? iconDeadline : iconDeadlineDark,
                className: "feature-ico adj-deadline",
                size: 75,
              },
              {
                title: "No Repairs Needed",
                body: "We buy houses as-is, no matter the condition.",
                alt: "no repairs needed icon",
                icon: isDark ? iconWrench : iconWrenchDark,
                className: "feature-ico",
                size: 75,
              },
              {
                title: "Cash Offers",
                body: "Avoid financing delays. Get paid in cash at closing.",
                alt: "money icon",
                icon: isDark ? iconMoney : iconMoneyDark,
                className: "feature-ico adj-money",
                size: 58,
              },
              {
                title: "Local and Trustworthy",
                body: "As a local Indy business, we offer in-person service.",
                alt: "trust icon",
                icon: isDark ? iconTrust : iconTrustDark,
                className: "feature-ico",
                size: 75,
              },
            ].map((item) => (
              <Box
                key={item.title}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
                sx={{ flex: "1 1 220px" }}
                p={2}
              >
                <Box mb={item.title === "Cash Offers" ? 2 : 0}>
                  <img
                    className={item.className}
                    alt={item.alt}
                    src={item.icon}
                    loading="lazy"
                    decoding="async"
                    width={item.size}
                    height={item.size}
                    style={{ height: `${item.size}px`, width: "auto" }}
                  />
                </Box>
                <Typography
                  fontSize="1.25rem"
                  fontWeight={600}
                  color="warning.main"
                >
                  {item.title}
                </Typography>

                <Typography fontSize="1rem" color={featureTextColor}>
                  {item.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" sx={{ px: { xs: 3, md: 7 }, py: 6 }}>
        <Box sx={{ maxWidth: 1350, mx: "auto", textAlign: "center" }}>
          <Typography
            component="h2"
            fontSize={{ xs: "1.75rem", md: "2.25rem" }}
            fontWeight={700}
            lineHeight={1.25}
            fontFamily="karla"
            mb={4}
          >
            How It Works
          </Typography>
          <Box display="flex" gap={3} justifyContent="center" flexWrap="wrap">
            {[
              {
                n: "1",
                t: "Schedule A Free Consultation.",
                d: "We personally visit your home to evaluate the property at no cost to you.",
                img: PEOPLE_CONSULTATION,
              },
              {
                n: "2",
                t: "Get your cash offer.",
                d: "After our visit, we'll make a fair cash offer that takes into account the condition and other factors.",
                img: PEOPLE_OFFER,
              },
              {
                n: "3",
                t: "Fast Closing.",
                d: "When we buy homes, we pay in cash. This skips the entire lending process so you can close in as little as 10 days.",
                img: PEOPLE_KEYS,
              },
            ].map(({ n, t, d, img }) => (
              <Box
                key={n}
                sx={{
                  flex: "1 1 280px",
                  borderRadius: 2,
                  boxShadow: 2,
                  bg: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.dark
                      : "#f8f8f8",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    height: 180,
                    backgroundImage: `url(${img})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      left: 12,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: "warning.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: "1rem",
                      boxShadow: 2,
                    }}
                  >
                    {n}
                  </Box>
                </Box>
                <Box sx={{ p: 3, pt: 2.5 }}>
                  <Typography fontSize="1.25rem" fontWeight={600}>
                    {t}
                  </Typography>
                  <Typography mt={1}>{d}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCtaClick("Get My Free Cash Offer - How It Works")}
            sx={{ ...primaryCtaSx, mt: 4 }}
          >
            GET MY FREE CASH OFFER
          </Button>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          px: { xs: 3, md: 7 },
          py: 6,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.primary.dark
              : "#e4e4e4",
        }}
      >
        <Box sx={{ maxWidth: 1350, mx: "auto" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: { xs: "flex-start", md: "space-between" },
              gap: 3,
              mb: 4,
            }}
          >
            <Box>
              <Typography
                component="h2"
                fontSize={{ xs: "1.75rem", md: "2.25rem" }}
                fontWeight={700}
                lineHeight={1.25}
                fontFamily="karla"
                mb={1}
              >
                We buy houses in any situation.
              </Typography>
              {SITUATIONS_DESCRIPTION && (
                <Typography
                  fontSize="1.05rem"
                  color="text.secondary"
                  maxWidth={520}
                  sx={{ lineHeight: 1.8 }}
                >
                  {SITUATIONS_DESCRIPTION}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              color="warning"
              onClick={handleCtaClick("Get My Free Cash Offer - Situations")}
              sx={{
                ...primaryCtaSx,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              GET MY FREE CASH OFFER
            </Button>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: "repeat(2, 1fr)", md: "repeat(6, 1fr)" }}
            gap={3}
          >
            {[
              {
                label: "Inheritance",
                sub: "Inherited or probate homes",
                img: PEOPLE_INHERITANCE,
              },
              {
                label: "Disrepair",
                sub: "Homes that need repairs",
                img: PEOPLE_DISREPAIR,
              },
              {
                label: "Foreclosure",
                sub: "Behind on payments",
                img: PEOPLE_FORECLOSURE,
              },
              {
                label: "Vacant",
                sub: "Tenant-occupied or vacant homes",
                img: PEOPLE_VACANT,
              },
              {
                label: "Fast Cash",
                sub: "Moving on a deadline",
                img: PEOPLE_FASTCASH,
              },
            ].map((item, i) => (
              <Box
                key={item.label}
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: 3,
                  height: { xs: 150, md: 180 },
                  backgroundColor: "#334155",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",

                  gridColumn: {
                    xs: i === 4 ? "span 2" : "span 1",
                    md: i < 3 ? "span 2" : "span 3",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url(${item.img})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(0deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.05) 100%)",
                    zIndex: 1,
                  }}
                />
                <Box
                  sx={{ position: "relative", zIndex: 2, p: 2, width: "100%" }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontFamily: "latoBold",
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 700,
                      textShadow: "0 1px 4px rgba(0,0,0,0.7)",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      fontSize: "0.85rem",
                      mt: 0.25,
                      textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                    }}
                  >
                    {item.sub}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          px: { xs: 3, md: 7 },
          py: 6,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.primary.main
              : theme.palette.common.white,
        }}
      >
        <Box sx={{ maxWidth: 1350, mx: "auto" }}>
          <Box textAlign="center" mb={5}>
            <Typography
              component="h2"
              fontSize={{ xs: "1.75rem", md: "2.25rem" }}
              fontWeight={700}
              lineHeight={1.25}
              fontFamily="karla"
              mb={1}
            >
              Seller Stories
            </Typography>
            <Typography color="text.secondary" maxWidth={560} mx="auto">
              Real homeowners in the Indianapolis area. Here is what selling to
              CarpHomes was like for them.
            </Typography>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "repeat(3, 1fr)" }}
            gap={3}
          >
            {[
              {
                q: "CarpHomes made selling my inherited house easy — no repairs, no hassle.",
                n: "Linda K.",
                nb: "Broad Ripple",
                img: testimonialLinda,
              },
              {
                q: "We needed to close fast before a job transfer. They delivered in 7 days.",
                n: "Marcus T.",
                nb: "Carmel",
                img: testimonialMarcus,
              },
              {
                q: "The offer was fair and they met us in person. I felt respected from day one.",
                n: "Diana R.",
                nb: "Fishers",
                img: testimonialDiana,
              },
            ].map(({ q, n, nb, img }) => (
              <Box
                key={n}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  textAlign: "left",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark"
                      ? t.palette.primary.main
                      : "#f8f8f8",
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: (t) =>
                    t.palette.mode === "dark"
                      ? "transparent"
                      : "rgba(0,0,0,0.07)",
                  boxShadow: (t) =>
                    t.palette.mode === "dark"
                      ? "none"
                      : "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <Typography
                  aria-label="5 out of 5 stars"
                  sx={{
                    color: "warning.main",
                    letterSpacing: "0.15em",
                    mb: 1.5,
                  }}
                >
                  ★★★★★
                </Typography>

                <Typography
                  fontStyle="italic"
                  lineHeight={1.75}
                  mb={3}
                  sx={{ flexGrow: 1 }}
                >
                  “{q}”
                </Typography>

                <Box display="flex" alignItems="center" gap={1.5}>
                  <img
                    src={img}
                    alt={`${n} from ${nb}`}
                    loading="lazy"
                    decoding="async"
                    width="48"
                    height="48"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      objectFit: "cover",
                      flexShrink: 0,
                      border: `2px solid ${props.theme.palette.warning.main}`,
                    }}
                  />
                  <Box>
                    <Typography fontWeight={700} lineHeight={1.3}>
                      {n}
                    </Typography>
                    <Typography fontSize="0.85rem" color="text.secondary">
                      {nb}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          <Box mt={4} textAlign="center">
            <Link
              to="/seller-stories"
              style={{
                fontWeight: 600,
                textDecoration: "underline",
                color: props.theme.palette.warning.main,
              }}
            >
              Read more seller stories
            </Link>
          </Box>

          <Box mt={4} textAlign="center">
            <Button
              variant="contained"
              color="warning"
              onClick={handleCtaClick(
                "Get My Free Cash Offer - Seller Stories",
              )}
              sx={primaryCtaSx}
            >
              GET MY FREE CASH OFFER
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          px: { xs: 3, md: 7 },
          py: 6,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.primary.light
              : "#e4e4e4",
        }}
      >
        <Box
          sx={{
            maxWidth: 1100,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 4, md: 6 },
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                position: "relative",
                width: { xs: 150, md: 190 },
                height: { xs: 150, md: 190 },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark"
                      ? t.palette.warning.main
                      : t.palette.secondary.main,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 9,
                  borderRadius: "50%",
                  overflow: "hidden",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                }}
              >
                <img
                  src={TravisHeadshot}
                  alt="Travis Carpenter, CEO of CarpHomes"
                  loading="lazy"
                  width="190"
                  height="190"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            </Box>

            <Box>
              <Typography
                component="h2"
                fontSize={{ xs: "1.35rem", md: "1.65rem" }}
                fontWeight={700}
                lineHeight={1.25}
                fontFamily="karla"
                mb={1.5}
              >
                Meet Travis Carpenter, CEO
              </Typography>
              <Typography
                lineHeight={1.85}
                fontSize="1rem"
                color="text.secondary"
              >
                CarpHomes is a local Indiana company that buys houses directly
                from homeowners, in any condition. No realtor commissions or
                closing costs, no open houses, and a member of our team meets
                you in person.
              </Typography>
              <Typography
                mt={2}
                fontFamily="latoBold"
                fontSize="0.95rem"
                color="warning.main"
              >
                As seen on WISH-TV!
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 12px 32px rgba(0,0,0,0.14)",
              backgroundColor: "#000",
              aspectRatio: "16 / 9",
            }}
          >
            {showVideo ? (
              <ReactPlayer
                url={Commercial}
                controls
                playing
                width="100%"
                height="100%"
                style={{ position: "absolute", inset: 0 }}
                /* Keeps the video inline on iOS instead of jumping to
                   fullscreen once the visitor taps play. This must be the
                   top-level `playsinline` prop — react-player v2 only
                   forwards `config.attributes` to the <video> element,
                   so a bare `attributes` prop is silently dropped. */
                playsinline
              />
            ) : (
              <Box
                component="button"
                onClick={() => setShowVideo(true)}
                aria-label="Play the CarpHomes TV commercial"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  padding: 0,
                  border: 0,
                  cursor: "pointer",
                  backgroundImage: `url(${commercial})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:focus-visible": {
                    outline: "3px solid",
                    outlineColor: "warning.main",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.45)",
                  }}
                />
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: 64, md: 80 },
                    height: { xs: 64, md: 80 },
                    borderRadius: "50%",
                    backgroundColor: "warning.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
                    transition: "transform 0.18s ease",
                    "&:hover": { transform: "scale(1.06)" },
                  }}
                >
                  <PlayArrowIcon
                    sx={{
                      color: "#fff",
                      fontSize: { xs: 36, md: 46 },
                      ml: 0.5,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.primary.dark
              : theme.palette.common.white,
          px: { xs: 3, md: 7 },
          py: 6,
        })}
      >
        <Box sx={{ maxWidth: 1000, mx: "auto" }}>
          <Typography
            component="h2"
            fontSize={{ xs: "1.75rem", md: "2.25rem" }}
            fontWeight={700}
            lineHeight={1.25}
            textAlign="center"
            fontFamily="karla"
            mb={1}
          >
            What Sets Us Apart
          </Typography>
          <Typography
            textAlign="center"
            color="text.secondary"
            maxWidth={560}
            mx="auto"
            mb={4}
          >
            See how selling directly to CarpHomes compares to listing with a
            real estate agent.
          </Typography>

          <Box
            sx={{
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(0,0,0,0.09)",
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.primary.main
                  : theme.palette.common.white,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "1.4fr 1fr 1fr" },
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.light
                    : "#f8f8f8",
                borderBottomWidth: 1,
                borderBottomStyle: "solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(0,0,0,0.09)",
              }}
            >
              <Box sx={{ p: { xs: 1.5, md: 2 } }} />
              <Box
                sx={{
                  p: { xs: 1.5, md: 2 },
                  borderLeftWidth: 1,
                  borderLeftStyle: "solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(0,0,0,0.09)",
                }}
              >
                <Typography
                  fontFamily="latoBold"
                  fontSize={{ xs: "0.9rem", md: "1.05rem" }}
                  color="warning.main"
                >
                  Selling to Us
                </Typography>
              </Box>
              <Box
                sx={{
                  p: { xs: 1.5, md: 2 },
                  borderLeftWidth: 1,
                  borderLeftStyle: "solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(0,0,0,0.09)",
                }}
              >
                <Typography
                  fontFamily="latoBold"
                  fontSize={{ xs: "0.9rem", md: "1.05rem" }}
                  sx={(theme) => ({
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.common.white
                        : theme.palette.primary.main,
                  })}
                >
                  Listing with an Agent
                </Typography>
              </Box>
            </Box>

            {[
              {
                q: "Commissions and closing costs?",
                us: "0%",
                agent: "More than 6%",
              },
              {
                q: "How long does it take to close?",
                us: "Within 10 Days",
                agent: "Usually 45-90 Days",
              },
              {
                q: "Do I need to clean or make repairs to my home?",
                us: "Never!",
                agent: "Always",
              },
              {
                q: "Does my home get listed on the MLS?",
                us: "Never!",
                agent: "Usually",
              },
              {
                q: "Can the deal fall through due to financing?",
                us: "Never!",
                agent: "Frequently",
              },
            ].map((row, i) => (
              <Box
                key={row.q}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "1.4fr 1fr 1fr" },
                  borderBottomWidth: i < 4 ? 1 : 0,
                  borderBottomStyle: "solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.06)",
                }}
              >
                <Box
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    gridColumn: { xs: "1 / -1", md: "auto" },
                  }}
                >
                  <Typography
                    fontWeight={600}
                    fontSize={{ xs: "0.9rem", md: "1rem" }}
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.main,
                    })}
                  >
                    {row.q}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(199,97,25,0.12)"
                        : "rgba(199,97,25,0.07)",
                    borderLeftWidth: 1,
                    borderLeftStyle: "solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(0,0,0,0.09)",
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: "warning.main",
                      fontSize: { xs: 16, md: 20 },
                    }}
                  />
                  <Typography
                    fontWeight={600}
                    fontSize={{ xs: "0.85rem", md: "0.95rem" }}
                    color="warning.main"
                  >
                    {row.us}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    borderLeftWidth: 1,
                    borderLeftStyle: "solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(0,0,0,0.09)",
                  }}
                >
                  <CancelIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.4)"
                          : "rgba(0,0,0,0.35)",
                      fontSize: { xs: 16, md: 20 },
                    }}
                  />
                  <Typography
                    fontSize={{ xs: "0.85rem", md: "0.95rem" }}
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.65)"
                          : "text.secondary",
                    })}
                  >
                    {row.agent}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box textAlign="center" mt={4}>
            <Button
              variant="contained"
              color="warning"
              onClick={handleCtaClick(
                "Get My Free Cash Offer - What Sets Us Apart",
              )}
              sx={primaryCtaSx}
            >
              GET MY FREE CASH OFFER
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.primary.main
              : "#e4e4e4",
        })}
      >
        <Box className="content-wrapper">
          <Box component="section" className="welcome-container">
            <Box sx={{ width: "100%", mx: "auto" }}>
              <Typography
                component="h2"
            fontFamily="karla"
            fontSize={{ xs: "1.75rem", md: "2.25rem" }}
            fontWeight={700}
            lineHeight={1.25}
                className="welcome-h1 heading"
                sx={(theme) => ({
                  fontFamily: "karla",
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.white
                      : theme.palette.primary.main,
                  fontSize: { xs: "1.9rem", md: "2.6rem" },
                  lineHeight: 1.15,
                  mb: "-0.15em",
                })}
              >
                Sell Your Indiana Home Fast and Hassle-Free with CarpHomes
              </Typography>

              <Typography
                className="welcome-h2 heading"
                sx={{
                  fontSize: { xs: "1.15rem", md: "1.4rem" },
                  lineHeight: { xs: "1.7rem", md: "2rem" },
                  color: "warning.main",
                  width: { xs: "100%", md: "80%" },
                  mt: { xs: 2, md: 3 },
                }}
              >
                CarpHomes is a local Indiana company that buys houses directly
                from homeowners, in any condition. No realtor commissions or
                closing costs, no open houses, and a member of our team meets
                you in person.
              </Typography>
            </Box>

            <Box sx={{ mt: { xs: "2.5em", md: "4em" } }}>
              <Button
                component={Link}
                to="/about"
                variant="outlined"
                color="warning"
                onClick={() => trackEvent("CTA", "Click", "About Us")}
                sx={{
                  px: { xs: 3, md: "25px" },
                  py: { xs: 1.25, md: "15px" },
                  minHeight: 48,
                  fontFamily: "latoBold",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  textDecoration: "none",
                  "&:hover": { textDecoration: "none" },
                }}
              >
                About Us
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        className="promise-container"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.primary.light
              : theme.palette.common.white,
          px: { xs: 3, md: 7 },
          py: 5,
        })}
      >
        <Box>
          <Typography
            component="h2"
            fontFamily="karla"
            fontSize={{ xs: "1.75rem", md: "2.25rem" }}
            fontWeight={700}
            lineHeight={1.25}
            textAlign="center"
            pt="4rem"
            sx={(theme) => ({
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.common.white
                  : theme.palette.primary.main,
            })}
          >
            Our Promise
          </Typography>

          <Typography
            component="h3"
            fontFamily="karla"
            fontSize={{ xs: "1.15rem", md: "1.35rem" }}
            fontWeight={700}
            lineHeight={1.25}
            textAlign="center"
            sx={(theme) => ({
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.common.white
                  : theme.palette.primary.main,
            })}
          >
            Why CarpHomes Stands Out
          </Typography>
        </Box>

        <Box className="content-wrapper">
          <Box mt={10} mb={10}>
            {[
              [
                "We're Invested in Our Community",
                "Most homes we purchase are renovated and restored to improve the neighborhood.",
              ],
              [
                "Personalized Customer Service",
                "We meet you face-to-face, answer your questions, and guide you every step of the way.",
              ],
              [
                "No Hidden Fees",
                "No commissions or closing costs - what we offer is what you'll receive.",
              ],
              [
                "Discretion and Respect",
                "We value your privacy and handle every transaction with integrity.",
              ],
            ].map(([title, body]) => (
              <Accordion
                defaultExpanded
                key={title}
                sx={(theme) => ({
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.dark
                      : theme.palette.grey.A100,
                })}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography
                    fontFamily="latoBold"
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.main,
                    })}
                  >
                    {title}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Typography
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.dark,
                    })}
                  >
                    {body}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.primary.dark
              : "#e4e4e4",
          px: { xs: 3, md: 7 },
          py: 6,
        })}
      >
        <Box sx={{ maxWidth: 1000, mx: "auto" }}>
          <Typography
            component="h2"
            fontSize={{ xs: "1.75rem", md: "2.25rem" }}
            fontWeight={700}
            lineHeight={1.25}
            textAlign="center"
            fontFamily="karla"
            mb={1}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            textAlign="center"
            color="text.secondary"
            maxWidth={560}
            mx="auto"
            mb={4}
          >
            Answers to the questions homeowners ask us most. Can&apos;t find
            what you&apos;re looking for? Call 317-812-1388.
          </Typography>

          <Box>
            {FAQS.map(({ q, a }, i) => (
              <Accordion
                key={q}
                id={`home-faq-${i + 1}`}
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.main
                      : theme.palette.grey.A100,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`home-faq-panel-${i + 1}`}
                  id={`home-faq-header-${i + 1}`}
                >
                  <Typography
                    fontFamily="latoBold"
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.main,
                    })}
                  >
                    {q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.dark,
                    })}
                  >
                    {a.split("\n\n").map((para, j) => (
                      <React.Fragment key={j}>
                        {j > 0 && <br />}
                        {para}
                      </React.Fragment>
                    ))}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          <Box textAlign="center" mt={4}>
            <Button
              variant="contained"
              color="warning"
              onClick={handleCtaClick("Get My Free Cash Offer - FAQ")}
              sx={primaryCtaSx}
            >
              GET MY FREE CASH OFFER
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        className="sell-my-home"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.secondary.main
              : theme.palette.primary.main,
          px: { xs: 3, md: 7 },
          py: 10,
          textAlign: "center",
        })}
      >
        <Typography
          component="h2"
            fontFamily="karla"
            fontSize={{ xs: "1.75rem", md: "2.25rem" }}
            fontWeight={700}
            lineHeight={1.25}
          sx={(theme) => ({
            color:
              theme.palette.mode === "dark"
                ? theme.palette.primary.main
                : theme.palette.common.white,
            fontSize: "2rem",
            fontFamily: "karla",
          })}
        >
          Ready to sell? Let us make you an offer today.
        </Typography>

        <Box sx={{ mt: "2.5em" }}>
          <Button
            variant="contained"
            color="warning"
            component={Link}
            to="/sell-my-home"
            sx={primaryCtaSx}
            onClick={() =>
              trackEvent("CTA", "Click", "Get Your Cash Offer Now")
            }
          >
            Get your Cash Offer Now
          </Button>
        </Box>
      </Box>

      <Box className="content-wrapper">
        <Box component="section" className="contact-form">
          <Box mt={15} mb={10}>
            <Typography
              component="h2"
            fontFamily="karla"
            fontSize={{ xs: "1.75rem", md: "2.25rem" }}
            fontWeight={700}
            lineHeight={1.25}
              sx={(theme) => ({
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : theme.palette.primary.main,
                fontSize: "2rem",
                fontFamily: "karla",
                mb: "0.5em",
                textAlign: "center",
              })}
            >
              Get Your Free, No-Obligation Cash Offer Today
            </Typography>

            <Form theme={props.theme} />
          </Box>

          <Box
            mt={15}
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === "dark"
                  ? theme.palette.primary.light
                  : theme.palette.secondary.main,
              borderRadius: "25px 25px 0 0",
            })}
          >
            {/* <Box className="contact-us-text-container">
              <Box display="flex" justifyContent="center" mt={5}>
                <Button
                  variant="contained"
                  sx={(theme) => ({
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.warning.main
                        : theme.palette.primary.main,
                  })}
                  onClick={() => trackEvent("CTA", "Click", "Reach Out")}
                >
                  <Link className="link-item" to="/contact">
                    <Typography color="common.white">Reach Out</Typography>
                  </Link>
                </Button>
              </Box>
            </Box> */}
          </Box>
        </Box>
      </Box>

      <Box
        component="nav"
        aria-label="Sticky call and offer bar"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          display: { xs: "flex", md: "none" },
          alignItems: "stretch",
          justifyContent: "space-around",
          backgroundColor: (theme) => theme.palette.primary.main,
          boxShadow: "0 -2px 10px rgba(0,0,0,0.15)",

          height: "calc(56px + env(safe-area-inset-bottom, 0px))",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",

          transform: showStickyBar ? "translateY(0)" : "translateY(110%)",
          transition: "transform 0.25s ease",
          visibility: showStickyBar ? "visible" : "hidden",
        }}
      >
        <Box
          component="a"
          href={PHONE_HREF}
          aria-label={`Call ${PHONE_NUMBER}`}
          onClick={() =>
            window.gtag?.("event", "call_click", {
              event_category: "Contact",
              event_label: "Sticky Bar Call Button",
            })
          }
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            textDecoration: "none",
            fontFamily: "latoBold",
            fontSize: "1rem",
          }}
        >
          Call Now
        </Box>
        <Button
          variant="contained"
          color="warning"
          onClick={handleStickyBarOffer}
          sx={{
            ...navCtaSx,
            flex: 1,
            mx: 1,
            my: 0.5,
            minHeight: 48,
          }}
        >
          Get My Offer
        </Button>
      </Box>

      <Footer />
    </div>
  );
};

export default Splash;